import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Lets a page push its title + subtitle up to the Topbar so the header
 * shows the page's own heading instead of the generic sidebar label. Any
 * component that renders `<SectionHeader>` writes here automatically.
 * Falls back to the sidebar label when no page has published anything.
 */
interface PageHeader {
  title: ReactNode | null;
  subtitle: ReactNode | null;
}

interface PageHeaderContextValue {
  header: PageHeader;
  setHeader: (h: PageHeader) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [header, setHeader] = useState<PageHeader>({ title: null, subtitle: null });
  const stableSet = useCallback((h: PageHeader) => setHeader(h), []);
  return (
    <PageHeaderContext.Provider value={{ header, setHeader: stableSet }}>
      {children}
    </PageHeaderContext.Provider>
  );
}

export function usePageHeader(): PageHeader {
  return useContext(PageHeaderContext)?.header ?? { title: null, subtitle: null };
}

/** Publish the given title/subtitle to the Topbar for as long as the caller is mounted. */
export function useSetPageHeader(title: ReactNode, subtitle?: ReactNode) {
  const ctx = useContext(PageHeaderContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setHeader({ title, subtitle: subtitle ?? null });
    return () => ctx.setHeader({ title: null, subtitle: null });
    // Serialising ReactNode is impractical; JSON-string strings/numbers or
    // toString() covers the common cases and avoids resetting on every render
    // when the caller inlines identical values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typeof title === 'string' || typeof title === 'number' ? title : String(title),
    typeof subtitle === 'string' || typeof subtitle === 'number' ? subtitle : String(subtitle),
  ]);
}
